import type { IdeaSurface } from './types';

/**
 * The use-case table of the idea composer (Kai, 2026-09-04: "one source of
 * truth in code configurable by use case"). Everything the surfaces
 * differ in lives here; the building blocks are the same everywhere.
 *
 *                      account   trial    slide   first_run
 *  doors (guided | own)  no       yes      no      no      (2026-09-09: same box + history behind both)
 *  chips + More/custom   yes      yes      no      yes     (slide: theme = the story just read)
 *  3 cards + ladder      yes      yes      no      cards   (first_run: compact, "Andere Ideen")
 *  wand above the box    no       no       yes     no      (the box is prefilled with the first idea;
 *  box auto-grows        no       no       yes     yes      Kai 2026-09-04: no extra "edit" tap)
 *  box on demand         no       no       no      yes     (behind an "Eigene Idee" pill, 2026-10-02)
 */
export interface SurfacePreset {
  chips: boolean;
  cards: boolean;
  /** A "next idea" wand + history in the box's header row instead of the label (one-tap surfaces). */
  wand: boolean;
  /** The box grows with its content so a prefilled idea is fully visible. */
  autoGrow: boolean;
  /** Two "doors" above everything (2026-09-09, Kai): "Inspirieren lassen" (default) shows chips +
   * cards, "Eigene Idee" shows the box + three tappable examples; one box and one history behind both. */
  doors: boolean;
  /** Cards without the length ladder and shuffle icon — one "Andere Ideen" button instead (trial). */
  compactCards: boolean;
  /** An "Eigene Idee" pill next to the wand: clears the box and focuses it (slide, 2026-09-09; a pill since 2026-09-13). */
  ownIdeaLink: boolean;
  /** The idea box stays folded behind an "Eigene Idee" pill next to "Andere Ideen" until that is tapped or the box holds
   *  typed text (first run, 2026-10-02: one question per screen — the mocks' "✎ Eigene Idee · 🎤"; a picked card is
   *  the idea, the card shows its text). */
  boxOnDemand: boolean;
  /** data-testid prefix for everything but the chips and the textarea. */
  prefix: string;
  chipsRowTestId: string;
  chipTestId: (key: string) => string;
  textareaTestId: string;
}

export const PRESETS: Record<IdeaSurface, SurfacePreset> = {
  account: {
    chips: true, cards: true, wand: false, autoGrow: false, doors: false, compactCards: false, ownIdeaLink: false, boxOnDemand: false,
    prefix: 'picker', chipsRowTestId: 'picker-chips', chipTestId: (key) => `picker-chip-${key}`, textareaTestId: 'theme-input',
  },
  trial: {
    chips: true, cards: true, wand: false, autoGrow: true, doors: true, compactCards: true, ownIdeaLink: false, boxOnDemand: false,
    prefix: 'try', chipsRowTestId: 'interest-chips-try', chipTestId: (key) => `try-interest-${key}`, textareaTestId: 'try-theme-input',
  },
  slide: {
    chips: false, cards: false, wand: true, autoGrow: true, doors: false, compactCards: false, ownIdeaLink: true, boxOnDemand: false,
    prefix: 'reader-trial', chipsRowTestId: 'reader-trial-chips', chipTestId: (key) => `reader-trial-chip-${key}`, textareaTestId: 'reader-trial-input',
  },
  // The native first run's step 3 (2026-10-02, docs/NATIVE-FIRST-RUN-2026-10.md): chips + three cards, no doors;
  // the box opens from the "Eigene Idee" pill next to "Andere Ideen".
  first_run: {
    chips: true, cards: true, wand: false, autoGrow: true, doors: false, compactCards: true, ownIdeaLink: false, boxOnDemand: true,
    prefix: 'first-run', chipsRowTestId: 'first-run-chips', chipTestId: (key) => `first-run-chip-${key}`, textareaTestId: 'first-run-theme-input',
  },
};
